import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import { useNavigate } from "react-router-dom";
import { addNewCourse, getAllCourses, editCourse } from "../../../../redux/features/courses/coursesService";
import { getAllCourseCategories } from "../../../../redux/features/courseCategories/courseCategoriesService";
import { getAllStaffMembers } from "../../../../redux/features/staff/staffService";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const CreateCourse = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const { isCourseCreating } = useSelector((state) => state.course.newCourse);

    const { courseCategories } = useSelector((state) => state.courseCategory.allCourseCategories);
    const { staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const [formData, setFormData] = useState({
        title: "",
    });

    const [selectedCategory, setSelectedCategory] = useState({
        id: '',
        name: ''
    });

    const [selectedUser, setSelectedUser] = useState({
        id: '',
        name: ''
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllCourses = useCallback(() => {
        dispatch(getAllCourses({
            skip: skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        dispatch(getAllCourseCategories({}));
        dispatch(getAllStaffMembers({
            role_name: 'mentor',
        }));
    }, [dispatch]);

    useEffect(() => {
        fetchAllCourses();
    }, [fetchAllCourses]);

    const handleSelect = (option) => {
        setFormData({ ...formData, courseCategoryId: option.id });
        setErrorMessages({ ...errorMessages, category: "" });
        setSelectedCategory({
            id: option.id,
            name: option.name
        });
    };

    const handleUserSelect = (option) => {
        setFormData({ ...formData, userId: option.id });
        setErrorMessages({ ...errorMessages, user: "" });
        setSelectedUser({
            id: option.id,
            name: option.name
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, file: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, file: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, file: "" });
            setFormData({ ...formData, file });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    // Validate input
    const isValidCourse = () => {
        const errors = {};
        const trimmedName = formData.title.trim();
        if (!trimmedName) {
            errors.title = "Name is required.";
        }
        if (!formData.slug) {
            errors.slug = "Slug is required.";
        }
        if (!formData.price) {
            errors.price = "Price is required.";
        }
        if (!formData.courseCategoryId) {
            errors.category = "Course Category is required.";
        }
        if (!formData.userId) {
            errors.user = "Mentor is required.";
        }
        if (!formData.file) {
            errors.file = "Thumbnail image is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewCourse = async () => {
        if (isValidCourse()) {
            setDisabledBtn(true);
            try {
                const response = await dispatch(addNewCourse(formData)).unwrap();
                setDisabledBtn(false);
                setFormData({
                    full_name: '',
                    email: '',
                    phone_number: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                showToast('success', response.message);
            }
            catch (error) {
                setDisabledBtn(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit staffMember
    const handleSelectedCourseChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCourse((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };


    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Course</h1>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>

                        {/* FULL NAME */}
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Title<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter Title"
                                        className="form-input-default"
                                    />
                                    {errorMessages.title && (
                                        <p className="text-danger">{errorMessages.title}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Slug<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleInputChange}
                                        placeholder="Enter Slug"
                                        className="form-input-default"
                                    />
                                    {errorMessages.slug && (
                                        <p className="text-danger">{errorMessages.slug}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        value={formData.description}
                                        name="description"
                                        onChange={handleInputChange}
                                        className="form-input-default"
                                        rows={3}
                                        placeholder="Describe the course"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">Read More</label>
                                    <textarea
                                        value={formData.read_more}
                                        onChange={handleInputChange}
                                        className="form-input-default"
                                        rows={3}
                                        placeholder="Read More"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Price<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        placeholder="Enter Price"
                                        className="form-input-default"
                                    />
                                    {errorMessages.price && (
                                        <p className="text-danger">{errorMessages.price}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">Course Category</label>
                                    <Dropdown
                                        options={courseCategories}
                                        placeholder="Choose an Option"
                                        value={selectedCategory.name}
                                        onSelect={handleSelect}
                                    />
                                    {errorMessages.category && (
                                        <p className={`text-danger`}>{errorMessages.category}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">Mentor</label>
                                    <Dropdown
                                        options={staffMembers.map(member => ({
                                            ...member,
                                            name: member.full_name
                                        }))}
                                        placeholder="Choose an Option"
                                        value={selectedUser.name}
                                        onSelect={handleUserSelect}
                                    />
                                    {errorMessages.user && (
                                        <p className={`text-danger`}>{errorMessages.user}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Image<span className={`required-star`}>*</span>
                                    </label>
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClick}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            style={{ display: "none" }}
                                        />
                                        {(!formData.file) ? <p>Drag & Drop a file here or click to select</p>
                                            :
                                            <div className={`inserted-file`}>
                                                <div className={`file-card`}>
                                                    <img src={URL.createObjectURL(formData.file)} alt='File Type' className={`left-icon`} />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {errorMessages.file && (
                                        <p className={`text-danger`}>{errorMessages.file}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className="btn-primary"
                            type="button"
                            disabled={disabledBtn}
                            onClick={insertNewCourse}
                        >
                            {isCourseCreating ? <span>Loading...</span> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateCourse;